import React, { useState, useEffect, useCallback, useRef } from "react";
import WidgetContainer from "../WidgetContainer.jsx";
import pandaNarrativeImg from "../../../../../assets/insight_panda.png";
import StakeholderChannelsScreen from "./Steps Building Narrative/StakeholderChannelsScreen.jsx";
import StakeholderNeedsSliderStep from "./Steps Building Narrative/StakeholderNeedsSliderStep.jsx";
import BuildNarrativeForm from "./Steps Building Narrative/BuildNarrativeForm.jsx";
import NarrativeCelebration from "../../Celebration/NarrativeCelebration";
import { progressService } from "../../../../../services/progressService";
import NarrativeSequence from "./Steps Building Narrative/NarrativeSequence.jsx";

const StakeholderChannelsStep = (props) => (
  <StakeholderChannelsScreen {...props} />
);

const StakeholderNeedsSliderStepWrapper = (props) => (
  <StakeholderNeedsSliderStep {...props} />
);

const BuildNarrativeFormStep = (props) => <BuildNarrativeForm {...props} />;

const NarrativeCelebrationStep = ({ onComplete, ...props }) => {
  React.useEffect(() => {
    onComplete?.();
  }, [onComplete]);
  return <NarrativeCelebration {...props} />;
};

const NarrativeSequenceStep = (props) => <NarrativeSequence {...props} />;


const BuildingNarrativeContainer = ({
  userId,
  subsectionId,
  onComplete,
  onPhaseChange,
}) => {
  const [selectedCard, setSelectedCard] = useState(null);
  const [selectedChannels, setSelectedChannels] = useState([]);
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [otherChannel, setOtherChannel] = useState("");
  const [, setShowCompletion] = useState(false);

  const [questionA, setQuestionA] = useState("");
  const [questionB, setQuestionB] = useState("");
  const [tasksByChannel, setTasksByChannel] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [currentChannelId, setCurrentChannelId] = useState(null);
  const isCompletedRef = useRef(false);


  const isValidUUID = (uuid) => {
    const uuidRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidRegex.test(uuid);
  };

  const loadProgress = useCallback(async () => {
    if (!userId || !subsectionId || !isValidUUID(subsectionId)) return;

    try {
      const record = await progressService.getUserProgress(userId, subsectionId);
      if (record?.data) {
        const payload = record.data.payload || record.data;

        setSelectedChannels(payload.selectedChannels || []);
        setOtherChannel(payload.otherChannel || "");

        if (payload.selectedLevel) setSelectedLevel(payload.selectedLevel);
        if (payload.selectedCard) setSelectedCard(payload.selectedCard);

        if (typeof payload.questionA === "string") setQuestionA(payload.questionA);
        if (typeof payload.questionB === "string") setQuestionB(payload.questionB);
        if (payload.tasksByChannel && typeof payload.tasksByChannel === "object") {
          setTasksByChannel(payload.tasksByChannel);
        }
      }
    } catch (err) {
      console.error("Failed to load narrative progress:", err);
    }
  }, [userId, subsectionId]);

  useEffect(() => {
    if (userId && subsectionId && isValidUUID(subsectionId)) {
      loadProgress();
    }
  }, [userId, subsectionId, loadProgress]);

  const buildPayload = useCallback(() => {
    let cleanedCard = null;
    if (selectedCard) {
      cleanedCard = {
        name: selectedCard.name,
        tagline: selectedCard.tagline,
        bullets: selectedCard.bullets,
        imgKey: selectedCard.imgKey || selectedCard.name || null,
      };
    }

    return {
      type: "widget",
      payload: {
        selectedChannels,
        otherChannel,
        selectedLevel,
        selectedCard: cleanedCard,
        questionA,
        questionB,
        tasksByChannel,
      },
    };
  }, [selectedChannels, otherChannel, selectedLevel, selectedCard, questionA, questionB, tasksByChannel]);

  const ensureCompleted = useCallback(async () => {
    if (!userId || !subsectionId || !isValidUUID(subsectionId)) return;
    if (isCompletedRef.current) return;
    try {
      const dataToSave = buildPayload();
      await progressService.saveMergedProgress(
        userId,
        subsectionId,
        dataToSave,
        "completed"
      );
      isCompletedRef.current = true;
    } catch (err) {
      console.error("Error saving narrative completion:", err);
    }
  }, [
    userId,
    subsectionId,
    // selectedChannels,
    // otherChannel,
    // selectedLevel,
    // selectedCard,
    // questionA,
    // questionB,
    // tasksByChannel,
    buildPayload
  ]);

  const saveProgressSilently = useCallback(async () => {
    if (!userId || !subsectionId || !isValidUUID(subsectionId)) return;
    if (isCompletedRef.current) return;

    setIsSaving(true);
    try {
      const dataToSave = buildPayload();
      await progressService.saveMergedProgress(
        userId,
        subsectionId,
        dataToSave,
        "in_progress"
      );
    } catch (err) {
      console.error("Narrative auto-save error:", err);
    } finally {
      setIsSaving(false);
    }
  }, [
    buildPayload,
    userId,
    subsectionId,
    // selectedChannels,
    // otherChannel,
    // selectedLevel,
    // selectedCard,
    // questionA,
    // questionB,
    // tasksByChannel, // <-- use tasksByChannel here
  ]);

  useEffect(() => {
    if (!userId || !subsectionId) return;
    const hasData =
      selectedChannels.length > 0 ||
      selectedCard ||
      questionA ||
      questionB ||
      Object.values(tasksByChannel).some(
        (arr) =>
          Array.isArray(arr) &&
          arr.some((t) => t.value || t.timeline || t.frequency)
      );
    if (!hasData) return;

    const timer = setTimeout(() => {
      saveProgressSilently();
    }, 2000);

    return () => clearTimeout(timer);
  }, [
    userId,
    subsectionId,
    selectedChannels,
    otherChannel,
    selectedLevel,
    selectedCard,
    questionA,
    questionB,
    tasksByChannel,
    saveProgressSilently,
  ]);

  const handleFinalComplete = async () => {
    await ensureCompleted();
    setShowCompletion(true);
  };

  const handleWidgetComplete = async () => {
    await ensureCompleted();
    onComplete?.();
  };

 

  const effectiveCurrentChannelId =
  currentChannelId || selectedChannels[0] || null;

  const currentTasks =
    effectiveCurrentChannelId && tasksByChannel[effectiveCurrentChannelId]
      ? tasksByChannel[effectiveCurrentChannelId]
      : [
          { value: "", timeline: "", frequency: "" },
          { value: "", timeline: "", frequency: "" },
          { value: "", timeline: "", frequency: "" },
          { value: "", timeline: "", frequency: "" },
        ];

  return (
    <WidgetContainer
      userId={userId}
      subsectionId={subsectionId}
      onComplete={handleWidgetComplete}
      onPhaseChange={onPhaseChange}
      intro={{
        image: pandaNarrativeImg,
        title: "Building Your Narrative",
        description:
          "You know who you must become. Let's ensure that your stakeholders see and believe in your evolved avatar.",
        buttonLabel: "Get Started",
      }}
      steps={[
        {
          id: "stakeholder_channels",
          title: "Stakeholder Channels",
          component: StakeholderChannelsStep,
          props: {
            selectedChannels,
            setSelectedChannels: (updater) => {
              setSelectedChannels((prev) => {
                const next = typeof updater === "function" ? updater(prev) : updater;
      
                setTasksByChannel((prevTasks) => {
                  const cleaned = {};
                  next.forEach((chId) => {
                    if (prevTasks[chId]) cleaned[chId] = prevTasks[chId];
                  });
                  return cleaned;
                });
      
                return next;
              });
            },
            otherChannel,
            setOtherChannel,
            isSaving,
          },
        },
        {
          id: "stakeholder_needs_slider",
          component: StakeholderNeedsSliderStepWrapper,
          props: {
            selectedLevel,
            onLevelSelect: ({ level, card }) => {
            setSelectedLevel(level);
            setSelectedCard(card);
            }
          },
        },
        {
          id: "build_narrative_form",
          title: "Build Your Narrative",
          component: BuildNarrativeFormStep,
          props: {
            selectedCard,
            selectedChannels,
            otherChannel,
            questionA,
            setQuestionA,
            questionB,
            setQuestionB,
            tasks: currentTasks,
            currentChannelId: effectiveCurrentChannelId,
            setCurrentChannelId,
            setTasks: (updater) => {
              if (!effectiveCurrentChannelId) return;
              setTasksByChannel((prev) => {
                const base = prev[effectiveCurrentChannelId] || currentTasks;
                const nextArray =
                  typeof updater === "function" ? updater(base) : updater;
                return {
                  ...prev,
                  [effectiveCurrentChannelId]: nextArray,
                };
              });
            },
            isSaving,
            selectedLevel,
            onNextStep: async () => {
              await handleFinalComplete();
            },
          },
        },
        {
          component: NarrativeCelebrationStep,
          fullScreen: true,
          props: {
            onComplete: ensureCompleted,
          },
        },
        {
          component: NarrativeSequenceStep,
          fullScreen: true,
          props: {
            selectedCard,
          },
        },
      ]}          
    />
  );
};

export default BuildingNarrativeContainer;
